import {
  Button,
  CloseButton,
  rem,
  SimpleGrid,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { closeModal, type ContextModalProps } from '@mantine/modals';
import { useTranslations } from 'next-intl';
import React, { useMemo } from 'react';

import { useBookingService } from '@/features/booking/components/services/use-booking-service';
import useIsMobile from '@/lib/is-mobile/use-is-mobile';
import { useBookingStore } from '@/stores/booking/booking-store-provider';

export interface InvalidServiceHourModalProps {
  message?: string;
}

const InvalidServiceHourModal: React.FC<
  ContextModalProps<InvalidServiceHourModalProps>
> = ({ id }) => {
  const isMobile = useIsMobile();
  const tPickupTime = useTranslations('booking.pickup_time');
  const tService = useTranslations('booking.service');
  const { selectedService, setIsOpenPickupTime } = useBookingStore(
    (state) => state
  );
  const { onOpenPopupAllService } = useBookingService({
    enabled: !isMobile,
  });

  const parseOpeningHourRange = (
    openingHour?: string
  ): { start: string; end: string } | null => {
    if (!openingHour) return null;

    const [startHour, endHour] = openingHour.split('-').map(Number);
    const pad = (n: number) => String(n).padStart(2, '0');

    return {
      start: `${pad(startHour)}:00`,
      end: `${pad(endHour)}:00`,
    };
  };

  const openingHourRange = useMemo(() => {
    if (!selectedService?.opening_hours) {
      return null;
    }

    return parseOpeningHourRange(selectedService.opening_hours);
  }, [selectedService?.opening_hours]);

  const handleChangeService = () => {
    closeModal(id);
    onOpenPopupAllService();
  };

  const handleChangeTime = () => {
    closeModal(id);
    setIsOpenPickupTime(true);
  };

  return (
    <>
      <CloseButton
        pos="absolute"
        iconSize={rem(24)}
        variant="transparent"
        onClick={() => closeModal(id)}
        c="neutral.6"
        top={rem(16)}
        style={
          isMobile
            ? {
                left: rem(16),
                outline: 'none',
              }
            : {
                right: rem(16),
                outline: 'none',
              }
        }
      />

      <Stack pt={rem(24)} gap={rem(8)}>
        <Title ta="center" order={3} c="neutral.7">
          {tPickupTime('out_of_service_hours')}
        </Title>

        <Text size="sm" fw={500} c="neutral.6" ta="center">
          {openingHourRange
            ? tPickupTime('service_hour_range', {
                start: openingHourRange.start,
                end: openingHourRange.end,
              })
            : tPickupTime('service_hour_unavailable')}
        </Text>
      </Stack>

      <SimpleGrid w="100%" cols={2} spacing="md" pt={rem(16)}>
        <Button
          variant="light"
          color="neutral.7"
          mih={rem(48)}
          radius={rem(4)}
          fw={700}
          onClick={handleChangeTime}
          size="sm"
          styles={{
            label: {
              color: 'var(--mantine-color-neutral-7)',
            },
          }}
        >
          {tPickupTime('schedule')}
        </Button>

        <Button
          mih={rem(48)}
          radius={rem(4)}
          fw={700}
          onClick={handleChangeService}
        >
          {tService('change_service')}
        </Button>
      </SimpleGrid>
    </>
  );
};

export default InvalidServiceHourModal;
